// Shared dropdown for "Default for new uploads" toolbar slot AND the
// orphan-chromatogram assignment rows. Mirrors the design's ConfigPicker.

import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import {
  instrumentVendorAccent,
  useInstrumentsCatalogs,
} from "@/hooks/useInstruments";
import type { InstrumentProfile } from "@/utils/types/instrument";

type ConfigPickerProps = {
  profiles: InstrumentProfile[];
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  placeholder?: string;
  width?: number;
  align?: "left" | "right";
};

export function ConfigPicker({
  profiles,
  value,
  onChange,
  label,
  placeholder = "Select configuration…",
  width = 220,
  align = "left",
}: ConfigPickerProps) {
  const { findInstrumentCatalog } = useInstrumentsCatalogs();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const current = profiles.find((p) => p.id === value) || null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="hstack gap-2"
      style={{ flexShrink: 0, position: "relative" }}
    >
      {label && (
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--ink-4)",
            letterSpacing: "0.06em",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="hstack gap-2"
        style={{
          padding: "5px 8px 5px 10px",
          borderRadius: 5,
          background: open ? "var(--bg-sunk)" : "var(--panel)",
          border: `1px solid ${open ? "var(--line-strong)" : "var(--line)"}`,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: current ? "var(--ink-2)" : "var(--ink-4)",
          cursor: "pointer",
          width,
          justifyContent: "space-between",
          transition: "background 100ms ease, border-color 100ms ease",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {current?.alias || placeholder}
        </span>
        <span
          style={{
            color: "var(--ink-4)",
            display: "inline-flex",
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 140ms ease",
          }}
        >
          <Icon name="chevron" size={11} />
        </span>
      </button>

      {open && (
        <div
          className="slide-up"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: align === "right" ? 0 : "auto",
            left: align === "right" ? "auto" : 0,
            minWidth: Math.max(width, 240),
            background: "var(--panel)",
            border: "1px solid var(--line)",
            borderRadius: 7,
            boxShadow: "var(--shadow-lift)",
            padding: 4,
            zIndex: 60,
            maxHeight: 280,
            overflow: "auto",
          }}
        >
          {profiles.length === 0 && (
            <div
              className="mono"
              style={{ padding: "10px 12px", fontSize: 11, color: "var(--ink-4)" }}
            >
              No configurations available
            </div>
          )}
          {profiles.map((p) => {
            const inst = findInstrumentCatalog(p.instrumentId);
            const isActive = p.id === value;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange(p.id);
                  setOpen(false);
                }}
                className="hstack gap-2"
                style={{
                  width: "100%",
                  padding: "7px 9px",
                  borderRadius: 4,
                  background: isActive ? "var(--accent-soft)" : "transparent",
                  border: `1px solid ${isActive ? "var(--accent-line)" : "transparent"}`,
                  textAlign: "left",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-sunk)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    flexShrink: 0,
                    background: instrumentVendorAccent(inst?.vendor),
                  }}
                />
                <div
                  className="vstack"
                  style={{ gap: 1, flex: 1, minWidth: 0, lineHeight: 1.25 }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--ink)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.alias}
                  </span>
                  <span
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: "var(--ink-4)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {inst?.vendor || "—"} · {inst?.label || "—"}
                  </span>
                </div>
                {isActive && (
                  <span
                    style={{ color: "var(--accent-ink)", display: "flex", flexShrink: 0 }}
                  >
                    <Icon name="check" size={12} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
